import {
  getBusinessErrorMessage,
  getSurfaceableError,
  getUserFacingApiMessageAsync,
} from '../../../utils/apiErrorDetail';
import { getHttpStatus } from '../../../utils/httpErrorUtils';
import { formatPhone } from '../../../utils/intake';

export type Notify = (message: string) => void;

export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

/** A US number as typed, in the E.164 form the API takes, or null. */
export const toE164 = (value: string): string | null => {
  const trimmed = value.trim();
  if (/^\+[1-9][0-9]{7,14}$/.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
};

export const displayPhone = (e164: string | null | undefined): string => {
  if (!e164) return '';
  return e164.startsWith('+1') ? formatPhone(e164) : e164;
};

/** Why a file cannot be sent as a port document, or null when it can. */
export const pdfProblem = (file: File): string | null => {
  if (file.type !== 'application/pdf') return 'Upload a PDF.';
  if (file.size > MAX_DOCUMENT_BYTES) {
    return 'The file must be 10 MB or smaller.';
  }
  return null;
};

// The porting API flags the 502s and 503s it writes for people as surfaceable.
export const describeError = (error: unknown, fallback: string): string =>
  getSurfaceableError(error)?.message ??
  getBusinessErrorMessage(error) ??
  fallback;

// A download answers with a blob, so its error body must be read first.
export const describeDownloadError = (
  error: unknown,
  fallback: string
): Promise<string> => getUserFacingApiMessageAsync(error, fallback);

// Never the error itself: its request config holds the body, which can carry
// the carrier PIN and account number, and session replay records the console.
export const logFailure = (message: string, error: unknown): void => {
  console.error(message, {
    status: getHttpStatus(error),
    code: getSurfaceableError(error)?.errorCode,
  });
};
